import React, { DragEvent, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Stack, Typography, alpha, styled } from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

interface FileUploadProps {
    onFileChange: (file: File | null) => void;
    /** Override the bold heading above the dropzone. Pass empty string to hide. */
    title?: string;
    /** Override the inline hint shown inside the dropzone. */
    primaryText?: string;
    /** Optional secondary hint (e.g. accepted formats). */
    secondaryText?: string;
    /** Native file-input `accept` attribute (e.g. ".pbf,.osm.pbf"). */
    accept?: string;
}

interface DropZoneProps {
    isDraggingOver: boolean;
    hasFile: boolean;
}

const DropZone = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isDraggingOver' && prop !== 'hasFile',
})<DropZoneProps>(({ theme, isDraggingOver, hasFile }) => {
    const accent = theme.palette.primary.main;
    const idleBorder = alpha(theme.palette.text.primary, 0.18);
    return {
        position: 'relative',
        borderRadius: theme.shape.borderRadius * 2,
        border: `1.5px dashed ${isDraggingOver ? accent : idleBorder}`,
        backgroundColor: isDraggingOver
            ? alpha(accent, 0.08)
            : hasFile
                ? alpha(accent, 0.04)
                : theme.palette.background.paper,
        padding: theme.spacing(4, 3),
        cursor: 'pointer',
        textAlign: 'center',
        color: theme.palette.text.secondary,
        transition: theme.transitions.create(
            ['border-color', 'background-color', 'transform', 'box-shadow'],
            { duration: theme.transitions.duration.shorter },
        ),
        boxShadow: isDraggingOver
            ? `0 0 0 4px ${alpha(accent, 0.12)}`
            : 'none',
        transform: isDraggingOver ? 'scale(1.01)' : 'none',
        '&:hover': {
            borderColor: accent,
            backgroundColor: alpha(accent, 0.04),
            color: theme.palette.text.primary,
        },
        '&:focus-visible': {
            outline: 'none',
            borderColor: accent,
            boxShadow: `0 0 0 4px ${alpha(accent, 0.18)}`,
        },
    };
});

const HiddenInput = styled('input')({
    display: 'none',
});

const formatBytes = (bytes: number): string => {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exp = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    const value = bytes / Math.pow(1024, exp);
    return `${value.toFixed(value >= 10 || exp === 0 ? 0 : 1)} ${units[exp]}`;
};

const FileUpload: React.FC<FileUploadProps> = ({
    onFileChange,
    title = 'Hier können Sie die CoreDump Datei zur Auswertung hochladen.',
    primaryText = 'Datei hierher ziehen oder klicken zum Auswählen',
    secondaryText,
    accept,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [isDraggingOver, setDraggingOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDraggingOver(false);
        const dropped = event.dataTransfer.files?.[0];
        if (dropped) setFile(dropped);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDraggingOver(true);
    };

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setDraggingOver(false);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const picked = event.target.files?.[0];
        if (picked) setFile(picked);
        // Reset so picking the same file twice still fires onChange.
        event.target.value = '';
    };

    useEffect(() => {
        onFileChange(file);
    }, [file]);

    const triggerFileInput = () => {
        fileInputRef.current?.click();
    };

    const handleClear = (event: React.MouseEvent) => {
        event.stopPropagation();
        setFile(null);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            triggerFileInput();
        }
    };

    return (
        <Box>
            {title && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    {title}
                </Typography>
            )}
            <DropZone
                role="button"
                tabIndex={0}
                isDraggingOver={isDraggingOver}
                hasFile={!!file}
                onClick={triggerFileInput}
                onKeyDown={handleKeyDown}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {file ? (
                    <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', justifyContent: 'center' }}>
                        <InsertDriveFileOutlinedIcon color="primary" sx={{ fontSize: 32 }} />
                        <Box sx={{ textAlign: 'left', minWidth: 0 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: 360,
                                }}
                                title={file.name}
                            >
                                {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatBytes(file.size)} · klicken zum Wechseln
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={handleClear}
                            aria-label="Auswahl zurücksetzen"
                            sx={{ ml: 1 }}
                        >
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    </Stack>
                ) : (
                    <Stack spacing={1} sx={{ alignItems: 'center' }}>
                        <CloudUploadOutlinedIcon
                            sx={{ fontSize: 44, color: isDraggingOver ? 'primary.main' : 'text.secondary' }}
                        />
                        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {primaryText}
                        </Typography>
                        {secondaryText && (
                            <Typography variant="caption" color="text.secondary">
                                {secondaryText}
                            </Typography>
                        )}
                    </Stack>
                )}
            </DropZone>
            <HiddenInput
                type="file"
                ref={fileInputRef}
                accept={accept}
                onChange={handleFileChange}
            />
        </Box>
    );
};

export default FileUpload;
